import { Link } from 'react-router-dom';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Badge } from '@cloudflare/kumo/components/badge';

// 2026 年度の申込詳細を表示。
// exhibitor_application_2026 (tableCount/chairCount/powerWatts/sponsorUnits/sponsorDisplayName)
// を yearDetail として親から受け取る。yearDetail が null の場合は旧データ (移行漏れ等)。

interface Year2026Detail {
  exhibitorId: string;
  tableCount: number;
  chairCount: number;
  powerWatts: number;
  sponsorUnits: number;
  sponsorDisplayName: string | null;
}

interface Props {
  exhibitor: {
    type: string;
    venueFeeTier: string | null;
    totalFee: number;
    finalVenueFee?: number | null;
    finalEquipmentFee?: number | null;
    finalPowerFee?: number | null;
    finalTotalFee?: number | null;
    isFree: boolean;
  };
  yearDetail: Year2026Detail | null;
}

export default function ExhibitorView2026({ exhibitor, yearDetail }: Props) {
  return (
    <Surface className="p-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-kumo-default">2026 年度 申込内容</h2>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <Row label="出展種別" value={typeLabel(exhibitor.type)} />
        <Row label="会場費ティア" value={exhibitor.venueFeeTier ?? '—'} />
      </div>

      <div className="my-4 h-px bg-kumo-line" />

      <h3 className="mb-2 text-sm font-semibold text-kumo-default">机・椅子・電源</h3>
      {yearDetail ? (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Row label="机" value={`${yearDetail.tableCount} 台`} />
          <Row label="椅子" value={`${yearDetail.chairCount} 脚`} />
          <Row
            label="電源"
            value={yearDetail.powerWatts > 0 ? `${yearDetail.powerWatts} W` : '不要'}
          />
        </div>
      ) : (
        <p className="text-sm text-kumo-muted">
          詳細テーブル (exhibitor_application_2026) のレコードがありません。
        </p>
      )}

      <div className="my-4 h-px bg-kumo-line" />

      {/* ここに出せるのは exhibitor_application_2026 の仮値 (申込時に入力され、支払い直前まで
          上書きされ続ける下書き) だけで、後追いの追加支援 (addon) を含まない。実績として
          誤読されると「スポンサー枠なし」と断定してしまうため、確定データは /sponsorships に
          一本化し、ここでは「申込時の希望」であることを明示する。 */}
      <h3 className="mb-2 text-sm font-semibold text-kumo-default">
        少額スポンサー (申込時の希望)
      </h3>
      {yearDetail && yearDetail.sponsorUnits > 0 ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Badge variant="outline">{yearDetail.sponsorUnits} 口を希望</Badge>
          <span className="text-kumo-default">
            表示名: {yearDetail.sponsorDisplayName ?? '(未設定)'}
          </span>
        </div>
      ) : (
        <p className="text-sm text-kumo-muted">申込時の希望なし</p>
      )}
      <p className="mt-2 text-xs text-kumo-muted">
        これは申込時に入力された希望で、入金実績ではありません。実際のご支援は{' '}
        <Link to="/sponsorships" className="underline">
          少額スポンサー
        </Link>{' '}
        ページで確認してください (後から追加された支援はここには出ません)。
      </p>

      <div className="my-4 h-px bg-kumo-line" />

      <h3 className="mb-2 text-sm font-semibold text-kumo-default">料金</h3>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Row label="申込時自動計算合計" value={`¥${exhibitor.totalFee.toLocaleString()}`} />
        <Row label="全額無料" value={exhibitor.isFree ? '有' : '無'} />
        <Row label="最終会場費 (override)" value={fmt(exhibitor.finalVenueFee)} />
        <Row label="最終備品費 (override)" value={fmt(exhibitor.finalEquipmentFee)} />
        <Row label="最終電源費 (override)" value={fmt(exhibitor.finalPowerFee)} />
        <Row label="最終合計 (override)" value={fmt(exhibitor.finalTotalFee)} />
      </div>
    </Surface>
  );
}

function typeLabel(t: string): string {
  switch (t) {
    case 'general':
      return '一般';
    case 'student-university':
      return '学生 (大学)';
    case 'student-high-school':
      return '学生 (高校以下)';
    case 'corporate':
      return '企業';
    default:
      return t;
  }
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-kumo-muted">{label}</span>
      <span className="text-sm text-kumo-default">{value}</span>
    </div>
  );
}

function fmt(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `¥${v.toLocaleString()}`;
}
